import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Dices } from 'lucide-react';
import {
  getPublicSpinWheel,
  spinWheel,
  SpinWheel,
} from '../../../api/payment/spin-wheel.api';

const formatCa = (v: number) => `${Number(v || 0).toLocaleString('vi-VN')} Ca`;

/**
 * Vòng quay trên trang creator: người xem trả Ca để quay, trúng ô nào thì
 * creator làm nhiệm vụ đó. Tỉ lệ chỉ hiện khi creator bật (BE quyết, không phải FE giấu).
 */
const CreatorSpinWheel: React.FC<{
  userId?: string;
  onAvailableChange?: (available: boolean) => void;
}> = ({ userId, onAvailableChange }) => {
  const [wheel, setWheel] = useState<SpinWheel | null>(null);
  const [spinning, setSpinning] = useState(false);
  const [lastResult, setLastResult] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    getPublicSpinWheel(userId)
      .then(setWheel)
      .catch(() => setWheel(null));
  }, [userId]);

  const available =
    !!userId && !!wheel?.exists && !!wheel.isActive && !!wheel.items?.length;

  // Báo lên cha (thanh tab Donate) biết tính năng này có đang hiện hay không, để ẩn/hiện tab
  // tương ứng. Dùng ref giữ callback mới nhất để effect chỉ chạy lại khi `available` đổi.
  const onAvailableChangeRef = useRef(onAvailableChange);
  onAvailableChangeRef.current = onAvailableChange;
  useEffect(() => {
    onAvailableChangeRef.current?.(available);
    // Unmount (vd đổi tab lớn rồi quay lại) -> báo về false, tránh cha giữ mãi trạng thái
    // available cũ (component đã biến mất nhưng cha vẫn tưởng tab này còn hiện được).
    return () => onAvailableChangeRef.current?.(false);
  }, [available]);

  if (!available || !wheel) return null;

  const handleSpin = () => {
    if (spinning) return; // chặn bấm 2 lần -> trừ Ca 2 lượt
    setSpinning(true);
    setLastResult(null);
    spinWheel(userId, wheel.spinPrice)
      .then((res) => {
        setLastResult(res.itemLabel);
        toast.success(`Bạn quay trúng: ${res.itemLabel}`, { duration: 5000 });
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Quay không thành công.');
        // Giá có thể vừa bị streamer đổi: tải lại để nút hiện đúng giá mới.
        getPublicSpinWheel(userId)
          .then(setWheel)
          .catch(() => undefined);
      })
      .finally(() => setSpinning(false));
  };

  return (
    <div className="mt-8 box rounded-2xl p-4 md:p-6">
      <div className="flex items-center gap-2">
        <Dices className="text-blue-600" size={20} />
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Vòng quay</h3>
      </div>

      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Quay trúng ô nào thì creator sẽ làm nhiệm vụ đó.
      </p>

      <ul className="mt-4 divide-y divide-gray-100 dark:divide-slate-700">
        {wheel.items.map((item, idx) => (
          <li key={idx} className="flex items-center justify-between gap-3 py-2">
            <span className="min-w-0 break-words text-sm text-gray-700 dark:text-gray-200">
              {item.label}
            </span>
            {typeof item.percent === 'number' && (
              <span className="shrink-0 text-xs font-semibold text-gray-500 dark:text-gray-400">
                {item.percent}%
              </span>
            )}
          </li>
        ))}
      </ul>

      {!wheel.showOdds && (
        <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
          Creator đang ẩn tỉ lệ trúng.
        </p>
      )}

      {lastResult && (
        <div className="mt-4 rounded-lg bg-blue-50 p-3 text-sm font-semibold text-blue-700 dark:bg-slate-700 dark:text-blue-300">
          🎡 Bạn quay trúng: {lastResult}
        </div>
      )}

      <button
        type="button"
        onClick={handleSpin}
        disabled={spinning}
        className="mt-4 w-full rounded-lg bg-blue-600 px-4 py-3 text-base font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
      >
        {spinning ? 'Đang quay…' : `Quay — ${formatCa(wheel.spinPrice)}`}
      </button>
    </div>
  );
};

export default CreatorSpinWheel;
